import { PetType } from '@orcar/common';

/**
 * 반려동물 동반("펫과 함께") 예약 관련 고객 안내/문구 상수.
 *
 * ⚠️ 임시 문구입니다. 제주로 최종 고객 안내 문구가 전달되면 이 파일만 교체하면 됩니다.
 * (가격/적격 로직은 백엔드 @orcar/common 상수에서 관리하므로 여기서 중복하지 않는다.)
 */

/**
 * 반려동물 동반 가능 "특정 차량모델" 화이트리스트.
 *
 * 시스템 기본 판정(@orcar/common isPetEligibleVehicle)은 "차종 + 연식(2020~2024)"
 * 기준이라 특정 모델만 콕 집을 수 없다. 냄새 클레임으로 제주로가 아래 표의 차량만
 * 펫 동반 가능으로 한정 요청 → 검색 펫 모드에서 모델명 부분일치로 추가 필터한다.
 *
 * 대상(제주로 전달 표):
 *   전기 코나 / 고급 더뉴그랜저 / 준중형 올뉴아반떼 CN7 / 중형 K5 /
 *   SUV 트레일블레이저·4세대 더올뉴투싼 / 승합 4세대카니발 9인승
 *
 * ⚠️ 아래 키워드는 실제 저장된 모델명과 부분일치(공백 무시, 대문자화)로 비교한다.
 *    표시 결과가 안 맞으면 이 배열의 키워드만 조정하면 된다.
 */
/**
 * 펫 동반 가능 차량 화이트리스트 — 차량모델 고유 ID로 정확히 지정.
 *
 * 이름/연식/연료로 추측 매칭하면 같은 이름의 다른 연식·연료(예: 24년 코나, 휘발유 그랜저)까지
 * 걸리므로, 제주로가 지정한 표의 차량을 모델 ID로 콕 집는다.
 *
 * 제주로 전달 표 기준 (2026-07 API 조회로 확정):
 *   1879  20년 코나(전기)             — 전기
 *   1871  20년 더 뉴 그랜저(LPG)       — 고급
 *   1882  21년 올뉴아반떼CN7(휘)       — 준중형
 *   1916  22년 K5(휘)                — 중형
 *   1899  21/22년 트레일블레이저(휘)    — SUV
 *   1884  21년 4세대 더올뉴투싼(휘)     — SUV
 *   1926  23년 4세대 카니발 9인승(경유)  — 승합
 *
 * ⚠️ 차량(모델) 교체·재등록으로 ID가 바뀌면 이 목록만 갱신하면 된다.
 */
export const PET_ELIGIBLE_MODEL_IDS: number[] = [
  1879, 1871, 1882, 1916, 1899, 1884, 1926,
];

/** 차량모델이 펫 동반 가능 화이트리스트에 포함되는지 (모델 ID 기준) */
export const isPetWhitelistedVehicleId = (id?: number | null): boolean =>
  id != null && PET_ELIGIBLE_MODEL_IDS.includes(id);

/** 반려동물 정보 입력 섹션 제목/설명 */
export const PET_FORM_TITLE = '반려동물 동반 정보';
export const PET_FORM_DESCRIPTION =
  '반려동물과 함께 이용하시는 경우, 아래 정보를 입력해주세요.';

/** 반려동물 종류 선택지 (강아지/고양이만 허용) */
export const PET_TYPE_OPTIONS: { value: PetType; label: string }[] = [
  { value: PetType.DOG, label: '강아지' },
  { value: PetType.CAT, label: '고양이' },
];
export const PET_TYPE_PLACEHOLDER = '선택해주세요';

/** 품종 입력 placeholder */
export const PET_BREED_PLACEHOLDER = '예: 푸들, 말티즈, 믹스견';

/** 무게/수량 안내 */
export const PET_WEIGHT_PLACEHOLDER = '예: 6';
export const PET_WEIGHT_GUIDE = '10kg 미만 소형 반려동물만 예약 가능합니다.';
export const PET_COUNT_GUIDE = '최대 2마리까지만 동반 가능합니다.';
export const PET_MAX_WEIGHT_KG = 10;
export const PET_MAX_COUNT = 2;

/** 필드별 에러 문구 */
export const PET_ERROR_TYPE = '반려동물 종류를 선택해주세요.';
export const PET_ERROR_BREED = '품종/종을 입력해주세요.';
export const PET_ERROR_WEIGHT_EMPTY = '반려동물 무게를 입력해주세요.';
export const PET_ERROR_WEIGHT_OVER = '10kg 미만만 예약 가능합니다.';
export const PET_ERROR_POLICY = '반려동물 이용 규정에 동의해야 결제할 수 있습니다.';

/** 이용 규정 동의 섹션 */
export const PET_POLICY_SECTION_TITLE = '반려동물 이용 규정 동의';
export const PET_POLICY_TOOLTIP =
  '반려동물과 함께 안전하고 쾌적하게 이용하시기 위한 필수 기준이에요. 케이지·카시트/패드·무게/수량·클리닝 비용 기준을 확인하고 모두 동의해야 결제할 수 있습니다.';
export const PET_POLICY_INFO_TITLE =
  '반려동물 이용 규정 동의 전에 꼭 확인해주세요';
export const PET_POLICY_INFO_DESCRIPTION =
  '케이지, 카시트/패드, 무게·수량 기준, 클리닝 비용 기준을 먼저 확인해야 합니다.';
export const PET_POLICY_INFO_BUTTON = '필수 준비사항 눌러서 보기';

/** 개별 필수 동의 체크 항목 (모두 체크해야 결제 가능) */
export const PET_POLICY_CHECKLIST: string[] = [
  '10kg 미만 소형 반려동물만 가능, 최대 2마리(2마리인 경우 각각 10kg 미만 소형 반려동물)임을 확인했습니다.',
  '반려동물은 케이지에 넣어야만 탑승 가능함을 확인했습니다.',
  '차량 내 카시트/패드/담요 사용 규정을 확인했습니다.',
  '털날림, 오염, 약취, 파손 시 클리닝 비용이 청구될 수 있음을 확인했습니다.',
];

/** 준비사항 시트(모달/바텀시트) 문구 */
export const PET_PREPARATION_TITLE = '반려동물 동반 시 꼭 확인해주세요';
export const PET_PREPARATION_DESCRIPTION =
  '반려동물과 함께 오실 경우 아래 준비사항이 필요합니다. 예약 전에 꼭 확인해주세요.';
export const PET_PREPARATION_CLOSE_LABEL = '닫기';
export const PET_PREPARATION_CONFIRM_LABEL = '이해했습니다!';

export type PetPreparationCard = {
  icon: string;
  title: string;
  description: string;
  tags: string[];
};

/** 준비사항 4카드 */
export const PET_PREPARATION_CARDS: PetPreparationCard[] = [
  {
    icon: '🧳',
    title: '케이지 필수 탑승',
    description:
      '반려동물은 케이지에 넣어야만 탑승 가능합니다. (공항 셔틀 탑승부터 본사 이동시에도 케이지내 필수)',
    tags: ['안전 확보 목적', '이동 중 이탈 방지'],
  },
  {
    icon: '🛋️',
    title: '카시트 / 패드 사용',
    description:
      '차량 내에서는 반려동물 전용 카시트 또는 패드/담요를 꼭 사용해주세요.',
    tags: ['시트 직접 접촉 방지', '털날림·오염 최소화'],
  },
  {
    icon: '⚖️',
    title: '무게 및 수량 기준',
    description:
      '10kg 미만 소형 반려동물만 가능하며, 최대 2마리(2마리인 경우 각각 10kg 미만)까지 동반 가능합니다.',
    tags: ['예약 시 무게 입력 필수', '기준 초과 제한 가능'],
  },
  {
    icon: '🧼',
    title: '클리닝 비용 기준',
    description:
      '털날림, 오염, 약취, 파손 발생 시 클리닝 비용 또는 수리비가 청구될 수 있습니다.',
    tags: ['단순 털날림: 10만원', '오염/악취/파손: 25만원 기준'],
  },
];
